import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { adminApi } from '../../lib/api';
import type { Product, CreateProductRequest } from '@sprintpulse/shared';
import { X, Loader2, AlertCircle } from 'lucide-react';

interface Props { product: Product | null; onClose: () => void; onSaved: () => void; }
interface Form { name: string; showRoadmap: boolean; jiraRoadmapProjectKey: string; roadmapColumnValue: string; }

const EMPTY: Form = { name: '', showRoadmap: false, jiraRoadmapProjectKey: '', roadmapColumnValue: 'Now' };

function productToForm(p: Product): Form {
  return { name: p.name, showRoadmap: !!p.jiraRoadmapProjectKey, jiraRoadmapProjectKey: p.jiraRoadmapProjectKey ?? '', roadmapColumnValue: p.roadmapColumnValue ?? 'Now' };
}

function Field({ label, hint, error, required, children }: {
  label: string; hint?: string; error?: string; required?: boolean; children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>
        {label}{required && <span style={{ color: 'var(--red)' }}> *</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs mt-1 opacity-60" style={{ color: 'var(--text-3)' }}>{hint}</p>}
      {error && (
        <p className="text-xs mt-1 flex items-center gap-1" style={{ color: 'var(--red)' }}>
          <AlertCircle size={10} />{error}
        </p>
      )}
    </div>
  );
}

const inputStyle = 'w-full px-3 py-2 rounded border text-sm outline-none transition-colors focus:ring-1';

export function ProductFormModal({ product, onClose, onSaved }: Props): JSX.Element {
  const isEdit = product !== null;
  const [form, setForm] = useState<Form>(isEdit ? productToForm(product) : EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [serverErr, setServerErr] = useState<string | null>(null);

  const set = (k: keyof Form) => (v: string) => setForm((p) => ({ ...p, [k]: v }));

  const validate = (): boolean => {
    const e: typeof errors = {};
    if (!form.name.trim()) e.name = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const createMut = useMutation({
    mutationFn: (req: CreateProductRequest) => adminApi.createProduct(req),
    onSuccess: onSaved,
    onError: (e: Error) => setServerErr(e.message),
  });
  const updateMut = useMutation({
    mutationFn: (req: { id: string; data: Partial<CreateProductRequest> }) =>
      adminApi.updateProduct(req.id, req.data),
    onSuccess: onSaved,
    onError: (e: Error) => setServerErr(e.message),
  });

  const pending = createMut.isPending || updateMut.isPending;

  const handleSubmit = (): void => {
    setServerErr(null);
    if (!validate()) return;
    const payload: CreateProductRequest = {
      name: form.name.trim(),
      jiraRoadmapProjectKey: form.showRoadmap ? (form.jiraRoadmapProjectKey.trim().toUpperCase() || undefined) : '',
      roadmapColumnValue: form.showRoadmap ? (form.roadmapColumnValue.trim() || undefined) : '',
    };
    if (isEdit) updateMut.mutate({ id: product.id, data: payload });
    else createMut.mutate(payload);
  };

  useEffect(() => {
    const h = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const inputCls = (err?: string): React.CSSProperties => ({
    background: 'var(--bg-subtle)',
    borderColor: err ? 'var(--red)' : 'var(--border)',
    color: 'var(--text-1)',
  });

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div
        className="relative rounded-lg border shadow-2xl w-full max-w-md mx-4 flex flex-col"
        style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
            {isEdit ? `Edit ${product.name}` : 'Add product'}
          </h2>
          <button onClick={onClose} className="opacity-40 hover:opacity-100 transition-opacity" style={{ color: 'var(--text-2)' }}>
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {serverErr && (
            <div className="rounded p-3 text-xs" style={{ background: 'var(--red-dim)', color: 'var(--red)' }}>
              {serverErr}
            </div>
          )}

          <Field label="Product name" required error={errors.name}>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name')(e.target.value)}
              placeholder="Product Name"
              className={inputStyle}
              style={inputCls(errors.name)}
            />
          </Field>

          {/* Roadmap toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.showRoadmap}
              onChange={(e) => setForm((p) => ({ ...p, showRoadmap: e.target.checked }))}
              className="rounded"
              style={{ accentColor: 'var(--accent)' }}
            />
            <span className="text-xs font-medium" style={{ color: 'var(--text-2)' }}>Show roadmap</span>
          </label>

          {form.showRoadmap && (
            <>
              <Field label="Jira roadmap project key" hint="e.g. JRM">
                <input
                  type="text"
                  value={form.jiraRoadmapProjectKey}
                  onChange={(e) => set('jiraRoadmapProjectKey')(e.target.value.toUpperCase())}
                  placeholder="e.g. JRM"
                  className={`${inputStyle} font-mono`}
                  style={inputCls()}
                />
              </Field>

              <Field label="Roadmap column filter" hint='Comma-separated — e.g. "Now" or "Now, Now - Committed"'>
                <input
                  type="text"
                  value={form.roadmapColumnValue}
                  onChange={(e) => set('roadmapColumnValue')(e.target.value)}
                  placeholder="Now"
                  className={inputStyle}
                  style={inputCls()}
                />
              </Field>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-6 py-4 border-t"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs rounded border transition-opacity hover:opacity-70"
            style={{ borderColor: 'var(--border)', color: 'var(--text-2)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={pending}
            className="flex items-center gap-2 px-4 py-2 text-xs rounded font-medium transition-opacity disabled:opacity-40 hover:opacity-80"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            {pending && <Loader2 size={12} className="animate-spin" />}
            {isEdit ? 'Save changes' : 'Add product'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
